/**
 * Admin-facing payloads — `/admin/v1/*` (PLAN §1.3, `docs/CONTRACT.md` §5).
 */
import { z } from "zod";
import { changeNumber, contextKey, epochMillis, flagKey, segmentName, slug, } from "./common.js";
import { flagDefinition } from "./definition.js";
export const ADMIN_ROLES = ["admin", "editor", "viewer"];
export const adminRole = z.enum(ADMIN_ROLES);
const isoTimestamp = z.string().datetime({ offset: true });
/* -------------------------------------------------------------- workspaces */
export const workspace = z
    .object({
    id: z.string(),
    /** The Spoonity vendorId this workspace belongs to (PLAN §3). */
    vendorId: slug,
    name: z.string().min(1).max(120),
    archived: z.boolean().default(false),
    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
})
    .strict();
export const workspaceCreate = z
    .object({ vendorId: slug, name: z.string().min(1).max(120) })
    .strict();
export const workspaceUpdate = z
    .object({ name: z.string().min(1).max(120).optional(), archived: z.boolean().optional() })
    .strict();
/* ------------------------------------------------------------ environments */
export const environment = z
    .object({
    id: z.string(),
    workspaceId: z.string(),
    key: slug,
    name: z.string().min(1).max(120),
    production: z.boolean().default(false),
    changeNumber,
    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
})
    .strict();
export const environmentCreate = z
    .object({
    key: slug,
    name: z.string().min(1).max(120),
    production: z.boolean().default(false),
})
    .strict();
/* ---------------------------------------------------------------- sdk keys */
export const SDK_KEY_KINDS = ["client", "server"];
export const sdkKeyKind = z.enum(SDK_KEY_KINDS);
/** `sdk_c_<26 chars of Crockford base32>` / `sdk_s_<26>` (CONTRACT §3.1). */
export const SDK_KEY_PATTERN = /^sdk_(c|s)_[0-9abcdefghjkmnpqrstvwxyz]{26}$/;
export const sdkKeySecret = z.string().regex(SDK_KEY_PATTERN);
export const sdkKey = z
    .object({
    id: z.string(),
    environmentId: z.string(),
    kind: sdkKeyKind,
    /** `sdk_c_01h4…` truncated to the first 12 characters, for display. */
    prefix: z.string(),
    name: z.string().min(1).max(120),
    lastUsedAt: isoTimestamp.nullable().default(null),
    revokedAt: isoTimestamp.nullable().default(null),
    createdAt: isoTimestamp,
})
    .strict();
export const sdkKeyCreate = z
    .object({ kind: sdkKeyKind, name: z.string().min(1).max(120) })
    .strict();
/** Only response that ever carries the secret; sha-256 is all the DB keeps. */
export const sdkKeyCreated = sdkKey.extend({ secret: sdkKeySecret }).strict();
/* ------------------------------------------------------------------- flags */
export const flag = z
    .object({
    key: flagKey,
    workspaceId: z.string(),
    name: z.string().min(1).max(160),
    description: z.string().max(2000).default(""),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    /** Free-form owner label (email or team), not an identity reference. */
    owner: z.string().max(160).default(""),
    archived: z.boolean().default(false),
    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
})
    .strict();
export const flagCreate = z
    .object({
    key: flagKey,
    name: z.string().min(1).max(160),
    description: z.string().max(2000).optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).optional(),
    owner: z.string().max(160).optional(),
})
    .strict();
export const flagUpdate = flagCreate.omit({ key: true }).partial().extend({
    archived: z.boolean().optional(),
});
/** `GET …/flags/:flag/definition` — the served definition plus admin metadata. */
export const flagDefinitionDetail = z
    .object({
    definition: flagDefinition,
    updatedAt: isoTimestamp,
    updatedBy: z.string().max(200),
    comment: z.string().max(500).optional(),
})
    .strict();
/* ---------------------------------------------------------------- segments */
export const segment = z
    .object({
    name: segmentName,
    environmentId: z.string(),
    description: z.string().max(2000).default(""),
    keyCount: z.number().int().nonnegative(),
    changeNumber,
    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
})
    .strict();
export const segmentCreate = z
    .object({ name: segmentName, description: z.string().max(2000).optional() })
    .strict();
export const segmentKeysPatch = z
    .object({
    add: z.array(contextKey).max(50_000).optional(),
    remove: z.array(contextKey).max(50_000).optional(),
    /** `true` replaces the whole membership list with `add`. */
    replace: z.boolean().optional(),
})
    .strict();
/* --------------------------------------------------------------- audit log */
export const CHANGE_ACTIONS = [
    "workspace.create",
    "workspace.update",
    "environment.create",
    "environment.update",
    "sdk_key.create",
    "sdk_key.revoke",
    "flag.create",
    "flag.update",
    "flag.archive",
    "flag.definition.update",
    "flag.kill",
    "flag.restore",
    "segment.create",
    "segment.update",
    "segment.delete",
    "segment.keys.update",
];
export const changeAction = z.enum(CHANGE_ACTIONS);
/**
 * Who performed a change (CONTRACT §2.3): a person signed in with Google
 * (`user`), a trusted service account acting as itself (`service`), or a
 * trusted service account acting for an operator it vouches for
 * (`delegated`, with `via` naming the service account).
 */
export const ACTOR_KINDS = ["user", "service", "delegated"];
export const actorKind = z.enum(ACTOR_KINDS);
export const changeEntry = z
    .object({
    id: z.string(),
    environmentId: z.string().nullable(),
    workspaceId: z.string(),
    action: changeAction,
    target: z.string(),
    actor: z.string().max(200),
    actorRole: adminRole,
    /** Absent on entries written before delegated identity existed (read as `user`). */
    actorKind: actorKind.optional(),
    /** The trusted service account a `delegated` actor came through; else `null`. */
    via: z.string().max(200).nullable().optional(),
    /** The `X-Spoonity-Vendor` a `delegated` actor was scoped to; else `null`. */
    vendorId: slug.nullable().optional(),
    comment: z.string().max(500).nullable().default(null),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    changeNumber,
    createdAt: isoTimestamp,
})
    .strict();
/* ---------------------------------------------------------------- insights */
export const impressionsSummaryBucket = z
    .object({
    /** Hour bucket, ISO 8601 UTC, e.g. `2026-09-23T14:00:00.000Z`. */
    hour: isoTimestamp,
    treatment: z.string().max(50),
    reason: z.string().max(40),
    count: z.number().int().nonnegative(),
})
    .strict();
export const impressionsSummary = z
    .object({
    flag: flagKey,
    from: isoTimestamp,
    to: isoTimestamp,
    total: z.number().int().nonnegative(),
    buckets: z.array(impressionsSummaryBucket),
})
    .strict();
export const eventsSummaryBucket = z
    .object({
    hour: isoTimestamp,
    eventType: z.string().max(80),
    count: z.number().int().nonnegative(),
    valueSum: z.number(),
})
    .strict();
export const eventsSummary = z
    .object({
    from: isoTimestamp,
    to: isoTimestamp,
    total: z.number().int().nonnegative(),
    buckets: z.array(eventsSummaryBucket),
})
    .strict();
/* ------------------------------------------------------- envelopes, config */
/** Cursor pagination envelope used by every admin list endpoint. */
export const pageQuery = z
    .object({ cursor: z.string().max(200).optional(), limit: z.coerce.number().int().min(1).max(200).default(50) })
    .strict();
export function pageOf(item) {
    return z
        .object({ items: z.array(item), nextCursor: z.string().nullable().default(null) })
        .strict();
}
export const page = z
    .object({ items: z.array(z.unknown()), nextCursor: z.string().nullable() })
    .strict();
/** `GET /admin/v1/config` — public, no auth. */
/** How the console signs people in (CONTRACT §2.4, §5.1). */
export const CONSOLE_AUTH_MODES = ["identity", "dev"];
export const adminConfig = z
    .object({
    /**
     * Deprecated (CONTRACT §2.4): always `""`. The console signs in against the
     * Spoonity Identity Server; the field stays for one release so an older
     * SPA build does not break, then goes.
     */
    googleClientId: z.string(),
    /**
     * `identity` — email + password against the Identity Server
     * (`/auth/v1/login`). `dev` — additionally offer the `fake:<email>` dev
     * token form (`ADMIN_AUTH_MODE=dev`, never in production).
     */
    authMode: z.enum(CONSOLE_AUTH_MODES).default("identity"),
    version: z.string(),
    /** Domains whose verified Google accounts get the editor role. */
    allowedDomains: z.array(z.string()),
    /** Present so the SPA can show the signed-in user's role after sign-in. */
    signedIn: z.boolean().default(false),
})
    .strict();
/** `GET /admin/v1/me` — who the presented token is and what it may do. */
export const adminIdentity = z
    .object({
    subject: z.string(),
    /** The Google email, or — when delegated — the `X-Spoonity-User` value. */
    email: z.string(),
    /** For a delegated caller, the header role capped at the service account's own. */
    role: adminRole,
    /** `true` when a trusted service account is acting for an operator (CONTRACT §2.3). */
    delegated: z.boolean().optional(),
    /** Delegated only: the one workspace (by `vendorId`) every route is scoped to. */
    vendorId: slug.optional(),
    /**
     * Delegated: the service account the request came through. Identity
     * session (CONTRACT §2.4): the literal `"identity"`.
     */
    via: z.string().optional(),
    /** Identity session only: display name (today the sign-in email). */
    name: z.string().optional(),
    /** Identity session only: the name of the vendor the session acts for. */
    vendorName: z.string().optional(),
    /** Identity session only: every vendor the Identity Server authorized at login. */
    allowedVendors: z.array(slug).optional(),
    /** Identity session only: `allowedVendors` with names and this person's role in each. */
    vendors: z
        .array(z.object({ id: slug, name: z.string(), role: adminRole }).strict())
        .optional(),
})
    .strict();
export const environmentEvaluateRequest = z
    .object({
    key: contextKey,
    attributes: z.record(z.string(), z.unknown()).optional(),
    flags: z.array(flagKey).max(500).optional(),
})
    .strict();
export const timestampOnly = z.object({ at: epochMillis }).strict();
//# sourceMappingURL=admin.js.map